import React from "react"
import Column from "@amzn/meridian/column"
import Alert from "@amzn/meridian/alert"
import Button from "@amzn/meridian/button"
import Text from "@amzn/meridian/text"
import { reportClientError } from "src/services/errorReporter"

interface Props { children: React.ReactNode }
interface State { hasError: boolean; error: Error | null }

/**
 * Per-route error boundary — catches render errors in individual pages
 * without crashing the entire app. Shows a retry button that resets the
 * error state (re-renders the page) instead of requiring a full reload.
 *
 * Used inside Suspense so that if a lazy-loaded page crashes, only that
 * page shows the error — the sidebar nav and header remain functional.
 */
class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      source: "RouteErrorBoundary",
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Column spacingInset="500" spacing="300">
          <Alert type="error">
            This page encountered an error: {this.state.error?.message || "Unknown error"}
          </Alert>
          <Text type="b200" color="secondary">
            The rest of the app is still working. You can try again or navigate to another page.
          </Text>
          <Button
            type="secondary"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </Button>
        </Column>
      )
    }
    return this.props.children
  }
}

export default RouteErrorBoundary
